import React, { useState } from 'react';
import { UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Staff-only "Act as installer" card shown at the top of Home (`/`).
 *
 * Lets any staff user enter a 6-char account code and hot-swap their session
 * for that installer's session. While impersonating, this card is hidden — the
 * persistent `ImpersonationBanner` takes over.
 */
export default function ImpersonateInstallerCard() {
  const { isStaff, isImpersonating, impersonate } = useAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isStaff || isImpersonating) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const res = await impersonate(trimmed);
      toast.success(`Now acting as ${res?.impersonating?.name || trimmed}`);
      setCode('');
    } catch (err) {
      toast.error(err.message || 'Could not switch to that installer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="impersonate-installer-card"
      className="mt-4 max-w-md mx-auto flex gap-2 items-stretch"
    >
      <input
        data-testid="impersonate-installer-code-input"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="Act as installer (6-char account code)"
        className="flex-1 border-2 border-amber-300 focus:border-amber-500 bg-amber-50 outline-none rounded-lg px-4 py-2 text-sm font-mono uppercase tracking-widest"
        maxLength={12}
        autoComplete="off"
      />
      <Button
        type="submit"
        disabled={submitting || code.trim().length < 4}
        data-testid="impersonate-installer-submit"
        className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-1.5"
      >
        <UserCheck className="w-4 h-4" />
        {submitting ? 'Switching…' : 'Act as'}
      </Button>
    </form>
  );
}
